import React, { useMemo } from 'react';
import { Flex, TextV2 } from '@withjoy/joykit';
import type { HotelRoomBlockPricingValues, StandardHotelPricingValues, AccommodationsConsoleState } from '@shared/types/Accommodations.types';
import { Skeleton } from '@shared/components/Skeleton';
import { useTranslation } from '@shared/core';
import { useFeatureValue } from '@shared/core/featureFlags';
import { DiscountBadge, DiscountBadgeWithTooltip } from './DiscountBadge';
import { useAccommodationsListingMobileLayout } from '@apps/stays/AccommodationsConsole/hooks/useAccommodationsListingMobileLayout';
import { hasOnsiteChargesValue, pickDisplayTotalString, useIsAllInTotalEnabled } from '@shared/hooks/useAllInTotal/useAllInTotal';

type AccommodationsDiscountTooltipPayload = Readonly<{
  minimumDiscountAmountToShow: number;
}>;

const isAccommodationsDiscountTooltipPayload = (value: unknown): value is AccommodationsDiscountTooltipPayload => {
  if (!value || typeof value !== 'object') return false;
  const payload = value as { minimumDiscountAmountToShow?: unknown };
  return typeof payload.minimumDiscountAmountToShow === 'number';
};

const CostBreakdown = ({
  pricingInfo,
  noOfNights,
  occupancies,
}: Readonly<{
  pricingInfo: StandardHotelPricingValues | HotelRoomBlockPricingValues;
  occupancies: AccommodationsConsoleState['occupancies'];
  noOfNights: number;
}>) => {
  const { isMobileOrTablet } = useAccommodationsListingMobileLayout();
  const {
    discountPercentage,
    nightlyBaseRateString,
    strikeoutNightlyBaseRateString,
    totalRateString,
    discountAmountString,
    discountAmount,
    allInTotalRateString,
    onsiteChargesTotalRate,
  } = pricingInfo;

  // Same arm `useListingSort` orders by, so the totals below read in the order the list is in.
  const isAllInTotalEnabled = useIsAllInTotalEnabled();
  // The string/major-unit variants of the shared selectors: this surface is handed formatted totals
  // and already-divided numbers, never a `DisplayMonetaryValue`, but the rules must stay the ones in
  // `allInTotal.ts` — in particular that an absent charge is "not determined", never zero.
  const displayTotalString = pickDisplayTotalString(totalRateString, allInTotalRateString, isAllInTotalEnabled);
  const includesPropertyCharges = isAllInTotalEnabled && hasOnsiteChargesValue(onsiteChargesTotalRate);

  const { tq } = useTranslation('accommodationsConsole');
  const { payload: accommodationsDiscountTooltipPayload } = useFeatureValue('accommodationsDiscountTooltipEnabled');
  const minimumDiscountAmountToShow = isAccommodationsDiscountTooltipPayload(accommodationsDiscountTooltipPayload)
    ? accommodationsDiscountTooltipPayload.minimumDiscountAmountToShow
    : 0;

  // The nights/rooms matrix is branched once and the arm chosen at each leaf. Each leaf is still a
  // whole sentence rather than a stem plus an interchangeable suffix: `tq` needs a complete literal
  // for the extractor to pick up, and a clause like "incl. taxes and fees" cannot be translated
  // independently of what precedes it in languages that inflect for case.
  const occupancyText = useMemo(() => {
    const unitCount = occupancies.unitCount;
    if (noOfNights === 1) {
      if (unitCount === 1) {
        return includesPropertyCharges ? tq`1 night, 1 room, incl. taxes, fees & property charges` : tq`1 night, 1 room, incl. taxes and fees`;
      }
      return includesPropertyCharges ? tq`1 night, ${unitCount} rooms, incl. taxes, fees & property charges` : tq`1 night, ${unitCount} rooms, incl. taxes and fees`;
    }
    if (unitCount === 1) {
      return includesPropertyCharges ? tq`${noOfNights} nights, 1 room, incl. taxes, fees & property charges` : tq`${noOfNights} nights, 1 room, incl. taxes and fees`;
    }
    return includesPropertyCharges
      ? tq`${noOfNights} nights, ${unitCount} rooms, incl. taxes, fees & property charges`
      : tq`${noOfNights} nights, ${unitCount} rooms, incl. taxes and fees`;
  }, [tq, noOfNights, occupancies.unitCount, includesPropertyCharges]);

  const hasDiscountStrings = !!(strikeoutNightlyBaseRateString && discountAmountString);
  const hasTooltipData = hasDiscountStrings && accommodationsDiscountTooltipPayload && discountAmount && discountAmount > minimumDiscountAmountToShow;

  return (
    <Flex
      flexDirection="column"
      alignItems="flex-end"
    >
      {discountPercentage &&
        (hasTooltipData ? (
          <DiscountBadgeWithTooltip
            totalPrice={nightlyBaseRateString}
            discountAmount={discountAmountString}
            label={tq`${discountPercentage}% off`}
            originalPrice={strikeoutNightlyBaseRateString}
          />
        ) : (
          <DiscountBadge label={tq`${discountPercentage}% off`} />
        ))}
      <Flex
        columnGap={1}
        alignItems="center"
        marginTop={discountPercentage ? 2 : 0}
      >
        {strikeoutNightlyBaseRateString && (
          <TextV2
            textDecoration="line-through"
            color="mono10"
            typographyVariant="label2"
          >
            {strikeoutNightlyBaseRateString}
          </TextV2>
        )}
        <TextV2
          typographyVariant={isMobileOrTablet ? 'hed1' : 'hed6'}
          fontWeight={600}
        >
          {nightlyBaseRateString}
        </TextV2>
        <TextV2
          typographyVariant={isMobileOrTablet ? 'label1' : 'label2'}
          color="mono10"
          fontWeight={400}
        >
          {tq`/ night`}
        </TextV2>
      </Flex>
      {displayTotalString && (
        <>
          <TextV2
            marginTop={1}
            textAlign="end"
            typographyVariant={isMobileOrTablet ? 'label1' : 'label2'}
            color="mono10"
          >
            {tq`${displayTotalString} total`}
          </TextV2>
          <TextV2
            marginTop={1}
            textAlign="end"
            typographyVariant="label1"
            color="mono10"
          >
            {occupancyText}
          </TextV2>
        </>
      )}
    </Flex>
  );
};

CostBreakdown.Skeleton = () => (
  <Flex
    rowGap="10px"
    alignItems="flex-end"
    flexDirection="column"
  >
    <Skeleton
      height={28}
      width={105}
    />
    <Skeleton
      height={16}
      width={80}
    />
    <Skeleton
      height={12}
      width={210}
    />
    <Flex
      width="100%"
      marginTop={5}
      justifyContent="center"
    >
      <Skeleton
        height={30}
        width={{ _: '100%', md: 200 }}
      />
    </Flex>
  </Flex>
);

export { CostBreakdown };
